import React, { useEffect } from 'react';
import { Avatar, Button, Card, Col, Row, Spin, Table, Tag, Typography } from 'antd';
import { UserOutlined, CalendarOutlined, FileTextOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelfService } from '../../hooks/useSelfService';
import HrStatCard from '../../components/HrStatCard';

const { Title, Text } = Typography;

const SelfServiceDashboardView: React.FC = () => {
  const navigate = useNavigate();
  const { profile, leaveRequests, leaveBalances, payslips, loading, fetchProfile, fetchMyLeaveRequests, fetchMyLeaveBalances, fetchMyPayslips } = useSelfService();

  useEffect(() => {
    fetchProfile();
    fetchMyLeaveRequests();
    fetchMyLeaveBalances();
    fetchMyPayslips();
  }, []);

  if (loading && !profile) return <Spin className="p-6" />;

  const pendingLeaves = leaveRequests.filter((r) => r.status === 'PENDING').length;
  const totalLeaveBalance = leaveBalances.reduce((sum, b) => sum + (b.remainingDays ?? 0), 0);
  const recentPayslips = payslips.slice(0, 3);

  const payslipColumns = [
    { title: 'Period', dataIndex: 'payrollPeriodName', key: 'period' },
    { title: 'Gross Pay', dataIndex: 'grossPay', key: 'grossPay', render: (v: number) => v?.toLocaleString() },
    { title: 'Net Pay', dataIndex: 'netPay', key: 'netPay', render: (v: number) => v?.toLocaleString() },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={s === 'PAID' ? 'green' : 'blue'}>{s}</Tag>,
    },
  ];

  return (
    <div className="p-6">
      {/* Profile Header */}
      <Card className="mb-6">
        <div className="flex items-center gap-4">
          <Avatar size={72} icon={<UserOutlined />} style={{ background: '#1890ff' }}>
            {profile ? `${profile.firstName?.[0]}${profile.lastName?.[0]}` : ''}
          </Avatar>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {profile ? `${profile.firstName} ${profile.lastName}` : 'My Self-Service Portal'}
            </Title>
            <Text type="secondary">{profile?.employeeNumber} · {profile?.positionTitle ?? ''} · {profile?.departmentName ?? ''}</Text>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard title="Pending Leaves" value={pendingLeaves} prefix={<CalendarOutlined />} valueStyle={{ color: '#faad14' }} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard title="Leave Balance (days)" value={totalLeaveBalance} prefix={<CalendarOutlined />} valueStyle={{ color: '#52c41a' }} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard title="Total Payslips" value={payslips.length} prefix={<FileTextOutlined />} valueStyle={{ color: '#1890ff' }} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <HrStatCard title="Leave Types Available" value={leaveBalances.length} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#722ed1' }} />
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col span={24}>
          <Card title="Quick Actions">
            <div className="flex gap-3 flex-wrap">
              <Button type="primary" icon={<CalendarOutlined />} onClick={() => navigate('/hr/self-service/leave')}>
                Apply for Leave
              </Button>
              <Button icon={<FileTextOutlined />} onClick={() => navigate('/hr/self-service/payslips')}>
                View Payslips
              </Button>
              <Button icon={<UserOutlined />} onClick={() => navigate('/hr/self-service/profile')}>
                My Profile
              </Button>
              <Button icon={<ClockCircleOutlined />} onClick={() => navigate('/hr/self-service/attendance')}>
                Attendance
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recent Payslips */}
      <Card
        title="Recent Payslips"
        extra={<Button type="link" onClick={() => navigate('/hr/self-service/payslips')}>View All</Button>}
      >
        <Table
          dataSource={recentPayslips}
          columns={payslipColumns}
          rowKey="id"
          size="small"
          pagination={false}
          onRow={(record) => ({ onClick: () => navigate(`/hr/self-service/payslips/${record.id}`) })}
          style={{ cursor: 'pointer' }}
        />
      </Card>
    </div>
  );
};

export default SelfServiceDashboardView;
